/** User-adjustable label layout and print specs. Stored in phomemo_settings.labelSpec. */
export interface LabelSpec {
  widthIn: number;
  heightIn: number;
  unit: 'in' | 'mm';
  marginPt: number;
  dpi: number;
  orientation: 'portrait' | 'landscape';
  titleFontPt: number;
  skuFontPt: number;
  locationFontPt: number;
  barcodeMaxWidthPt: number;
  barcodeHeightPt: number;
}

export const DEFAULT_LABEL_SPEC: LabelSpec = {
  widthIn: 4,
  heightIn: 6,
  unit: 'in',
  marginPt: 18,
  dpi: 203,
  orientation: 'portrait',
  titleFontPt: 12,
  skuFontPt: 10,
  locationFontPt: 10,
  barcodeMaxWidthPt: 200,
  barcodeHeightPt: 50,
};

const LABEL_SPEC_BOUNDS = {
  widthIn: [0.5, 12] as [number, number],
  heightIn: [0.5, 18] as [number, number],
  marginPt: [0, 72] as [number, number],
  dpi: [203, 300] as [number, number],
  titleFontPt: [6, 24] as [number, number],
  skuFontPt: [6, 20] as [number, number],
  locationFontPt: [6, 20] as [number, number],
  barcodeMaxWidthPt: [50, 400] as [number, number],
  barcodeHeightPt: [20, 120] as [number, number],
};

function clamp<T extends keyof typeof LABEL_SPEC_BOUNDS>(
  key: T,
  value: number
): number {
  const [min, max] = LABEL_SPEC_BOUNDS[key];
  return Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));
}

/** Clamp a numeric LabelSpec field for UI validation. */
export function clampLabelSpecValue(
  key: keyof typeof LABEL_SPEC_BOUNDS,
  value: number
): number {
  return clamp(key, value);
}

/** Merge partial LabelSpec with defaults; clamp numerics to bounds. */
export function mergeLabelSpec(partial?: Partial<LabelSpec> | null): LabelSpec {
  const base = { ...DEFAULT_LABEL_SPEC };
  if (!partial || typeof partial !== 'object') return base;
  const o = partial as Record<string, unknown>;
  if (typeof o.widthIn === 'number') base.widthIn = clamp('widthIn', o.widthIn);
  if (typeof o.heightIn === 'number') base.heightIn = clamp('heightIn', o.heightIn);
  if (o.unit === 'in' || o.unit === 'mm') base.unit = o.unit;
  if (typeof o.marginPt === 'number') base.marginPt = clamp('marginPt', o.marginPt);
  if (typeof o.dpi === 'number') base.dpi = clamp('dpi', o.dpi);
  if (o.orientation === 'portrait' || o.orientation === 'landscape') base.orientation = o.orientation;
  if (typeof o.titleFontPt === 'number') base.titleFontPt = clamp('titleFontPt', o.titleFontPt);
  if (typeof o.skuFontPt === 'number') base.skuFontPt = clamp('skuFontPt', o.skuFontPt);
  if (typeof o.locationFontPt === 'number') base.locationFontPt = clamp('locationFontPt', o.locationFontPt);
  if (typeof o.barcodeMaxWidthPt === 'number') base.barcodeMaxWidthPt = clamp('barcodeMaxWidthPt', o.barcodeMaxWidthPt);
  if (typeof o.barcodeHeightPt === 'number') base.barcodeHeightPt = clamp('barcodeHeightPt', o.barcodeHeightPt);
  return base;
}

/** Printer command language: ESC/POS raster (bitmap row) vs TSPL/ZPL (label languages). */
export type PrinterProtocol = 'escpos' | 'tspl_zpl';

/** BLE printer connection mode: phomemo (FF00), generic (FFE0/NUS), or any (all devices). */
export type PrinterMode = 'phomemo' | 'generic' | 'any';

/** Persisted Phomemo printer settings (e.g. in user_settings or localStorage) */
export interface PhomemoSettings {
  deviceId?: string;
  deviceName?: string;
  autoReconnect?: boolean;
  /** When true (default), scan all BLE services for a writable characteristic if known pairs fail. */
  autoSelectWritableCharacteristic?: boolean;
  /** BLE connection mode: filter by service UUID for reliable connection. Default: 'phomemo'. */
  printerMode?: PrinterMode;
  /** Printer language: ESC/POS raster or TSPL/ZPL. */
  printerProtocol?: PrinterProtocol;
  density?: number; // 0–100 or printer-specific scale
  barcodeSafeMode?: boolean;
  /** @deprecated Use labelSpec.marginPt for PDF layout. */
  margin?: number;
  labelSpec?: Partial<LabelSpec>;
}

export type PhomemoConnectionState = 'disconnected' | 'connecting' | 'connected';

export interface ScannedDevice {
  deviceId: string;
  name?: string;
}
